import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Coins, Share2, Palette, Megaphone, Wallet as WalletIcon, ArrowLeft, Sparkles,
} from "lucide-react";
import http from "@/api";
import { useApp } from "@/context";

const WAYS = [
  {
    icon: Share2,
    to: "/my-designs",
    color: "#0ea5e9",
    ar: ["شارك تصميمك واربح عمولة", "صمّم باقتك بالذكاء وشارك رابطها — كل طلب يُسلَّم عبر تصميمك يربحك عمولة تبدأ من 10% وترتفع حتى 20% كل ما زادت طلباتك.", "10% → 20% لكل تصميم"],
    en: ["Share your design, earn commission", "Design with AI and share the link — every delivered order via your design earns you commission starting at 10% and growing to 20%.", "10% → 20% per design"],
    testId: "earn-way-share",
  },
  {
    icon: Palette,
    to: "/my-designs",
    color: "#10b981",
    ar: ["صمّم وبِع مع RG", "ارفع تصميمك الحقيقي، وبعد اعتماده يُعرض لعملاء RG وتربح عمولة متدرجة من كل عملية بيع تُسلَّم.", "عمولة تتصاعد حتى 20%"],
    en: ["Design & sell with RG", "Upload your real design — once approved it goes live for RG customers and you earn a tiered commission on every delivered sale.", "Tiered up to 20%"],
    testId: "earn-way-sell",
  },
  {
    icon: Megaphone,
    to: "/influencer-program",
    color: "#d4af37",
    ar: ["كن سفير RG بكود خاص", "للمؤثرين والمسوّقين: كود خصم باسمك — عملاؤك يحصلون على مزايا، وأنت تربح عمولة من كل طلب عبر كودك.", "كود + عمولة"],
    en: ["Become an RG ambassador", "For influencers & marketers: your own promo code — your audience gets perks, you earn commission on every order.", "Code + commission"],
    testId: "earn-way-ambassador",
  },
];

/* «اربح مع RG» — للجميع وليس حصراً على المؤثرين */
export default function EarnHub() {
  const { lang, user } = useApp();
  const ar = lang === "ar";
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    if (user) http.get("/wallet/me").then(({ data }) => setWallet(data)).catch(() => {});
  }, [user]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="earn-hub-page">
      {/* المقدمة */}
      <div className="rounded-2xl overflow-hidden p-5 sm:p-7 text-white"
           style={{ background: "linear-gradient(130deg, #0b2f22, #105438 55%, #14603f)" }}>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-300/90 font-bold">
          <Sparkles size={13} /> {ar ? "للجميع — بلا شروط" : "For everyone — no conditions"}
        </div>
        <h1 className={`${ar ? "font-display-ar" : "font-display-en"} text-2xl sm:text-3xl mt-1.5 leading-snug`}>
          {ar ? "اربح مع RG" : "Earn with RG"}
        </h1>
        <p className="text-[13px] text-white/80 mt-1.5 leading-relaxed">
          {ar
            ? "ثلاث طرق حقيقية للربح — اختر ما يناسبك: شارك، صمّم، أو سوّق. كل واحد يقدر يستفيد، مو شرط تكون بلوقر."
            : "Three real ways to earn — share, design, or promote. Anyone can benefit, you don't have to be an influencer."}
        </p>
        {user && (
          <Link to="/wallet"
                className="mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-bold text-emerald-950"
                style={{ background: "linear-gradient(90deg, #e9c97a, #d4af37)" }}
                data-testid="earn-wallet-link">
            <WalletIcon size={15} />
            {ar
              ? `محفظتك: ${(wallet?.balance ?? 0).toLocaleString()} نقطة`
              : `Your wallet: ${(wallet?.balance ?? 0).toLocaleString()} pts`}
            <ArrowLeft size={14} className={ar ? "" : "rotate-180"} />
          </Link>
        )}
      </div>

      {/* طرق الربح */}
      <div className="space-y-3">
        {WAYS.map((w, i) => {
          const [title, desc, badge] = ar ? w.ar : w.en;
          return (
            <Link key={i} to={w.to}
                  className="card-soft p-4 flex items-start gap-3.5 block hover:shadow-md transition-shadow active:scale-[0.99]"
                  data-testid={w.testId}>
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
                   style={{ background: `${w.color}18`, color: w.color }}>
                <w.icon size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-[14.5px]">{title}</span>
                  <span className="text-[9.5px] font-extrabold px-2 py-0.5 rounded-full"
                        style={{ background: `${w.color}15`, color: w.color }}>
                    {badge}
                  </span>
                </div>
                <p className="text-[12.5px] opacity-70 mt-1 leading-relaxed">{desc}</p>
              </div>
              <ArrowLeft size={16} className={`opacity-40 shrink-0 mt-2 ${ar ? "" : "rotate-180"}`} />
            </Link>
          );
        })}
      </div>

      {!user && (
        <Link to="/login?next=/earn" className="btn-primary w-full justify-center"
              data-testid="earn-login-btn">
          <Coins size={16} /> {ar ? "سجّل دخولك وابدأ الربح" : "Sign in & start earning"}
        </Link>
      )}
    </div>
  );
}
